import { Component, OnDestroy, OnInit } from "@angular/core";
import { PortfolioService } from "../portfolio/portfolio.service";
import { PortfolioModel } from "../../shared/models/portfolio.model";
import { AuthService } from "../../shared/services/auth.service";
import {
  NavigationEnd,
  Router,
  RouterModule,
  ActivatedRoute,
} from "@angular/router";
import { MaterialModule } from "src/app/app.materials";
import { FormsModule } from "@angular/forms";
import { ThemeService, AppTheme } from "../../shared/services/theme.service";
import {
  LayoutService,
  ContainerWidth,
} from "../../shared/services/layout.service";

import { Subscription } from "rxjs";

interface PortfolioTab {
  id: string;
  label: string;
  /** Built lazily because the portfolio id isn't known at construction time. */
  link: (portfolioId: string | number) => any[];
  /** True if the active URL matches this tab. */
  match: (url: string) => boolean;
}

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  standalone: true,
  imports: [MaterialModule, FormsModule, RouterModule],
})
export class HomeComponent implements OnInit, OnDestroy {
  portfolio = new PortfolioModel({});
  isAdmin = false;
  isPortfolio = false;
  /** Portfolio ID extracted directly from the URL — available immediately on
   *  hard refresh, before the async portfolio service resolves. */
  portfolioIdFromUrl: string | number | null = null;
  pageTitle = "Portfolios";

  theme: AppTheme = "dark";
  containerWidth: ContainerWidth = "fluid";

  /** Step-tabs rendered inside the topbar when in portfolio mode.
   *  Order matches the prototype's topbar tabs. */
  readonly portfolioTabs: PortfolioTab[] = [
    {
      id: "edit",
      label: "Edit Portfolio",
      link: (id) => ["/app/portfolio", id],
      match: (url) => /\/app\/portfolio\/[^/]+(\?|$)/.test(url),
    },
    {
      id: "backtest",
      label: "Backtest",
      link: (id) => ["/app/portfolio-view", id, "overview"],
      match: (url) =>
        /\/app\/portfolio-view\/[^/]+(\/(overview)?)?(\?|$)/.test(url),
    },
    {
      id: "optimization",
      label: "Optimization",
      link: (id) => ["/app/portfolio-view", id, "optimization"],
      match: (url) =>
        /\/app\/portfolio-view\/[^/]+\/optimization(\?|$)/.test(url),
    },
    {
      id: "risk",
      label: "Risk Analysis",
      link: (id) => ["/app/portfolio-view", id, "factor-analysis"],
      match: (url) =>
        /\/app\/portfolio-view\/[^/]+\/factor-analysis(\?|$)/.test(url),
    },
  ];

  private currentUrl = "";

  private themeSub?: Subscription;
  private layoutSub?: Subscription;
  private routerSub?: Subscription;
  private portfolioSub?: Subscription;
  private userSub?: Subscription;

  constructor(
    private router: Router,
    private service: PortfolioService,
    private auth: AuthService,
    private themeService: ThemeService,
    private layoutService: LayoutService,
  ) {}

  ngOnInit() {
    this.theme = this.themeService.current;
    this.themeSub = this.themeService.current$.subscribe(
      (t) => (this.theme = t),
    );

    this.containerWidth = this.layoutService.current;
    this.layoutSub = this.layoutService.current$.subscribe(
      (w) => (this.containerWidth = w),
    );

    this.currentUrl = this.router.url || "";
    this.applyRoute(this.currentUrl);

    this.routerSub = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        const url = event.url || "";
        this.currentUrl = url;
        this.applyRoute(url);
      }
    });

    this.portfolioSub = this.service.portfolio$.subscribe((res) => {
      this.portfolio = res;
      this.pageTitle = this.resolveTitle(this.currentUrl);
    });

    this.userSub = this.auth.getCurrentUser().subscribe((user: any) => {
      if (user) {
        this.isAdmin = user.isSuperAdmin();
      } else {
        this.router.navigate(["app/lo"]);
      }
    });
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.layoutSub?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.portfolioSub?.unsubscribe();
    this.userSub?.unsubscribe();
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  toggleContainerWidth(): void {
    this.layoutService.toggle();
  }

  onLogout(): void {
    this.auth.logout(true);
  }

  isTabActive(tab: PortfolioTab): boolean {
    return tab.match(this.currentUrl);
  }

  private applyRoute(url: string): void {
    const isPortfolio =
      /\/app\/portfolio(\/[^/]+)?($|\?)/.test(url) ||
      /\/app\/portfolio-view\//.test(url);

    if (!isPortfolio) {
      this.service.clear();
      this.portfolioIdFromUrl = null;
    } else {
      // Extract the portfolio ID from the URL immediately so the tab nav
      // renders on hard refresh without waiting for the async service.
      const viewMatch = url.match(/\/app\/portfolio-view\/([^/?]+)/);
      const editMatch = url.match(/\/app\/portfolio\/([^/?]+)/);
      const id = viewMatch?.[1] ?? editMatch?.[1] ?? null;
      if (id && id !== "create") {
        this.portfolioIdFromUrl = id;
      }
    }

    this.isPortfolio = isPortfolio;
    this.pageTitle = this.resolveTitle(url);
  }

  private resolveTitle(url: string): string {
    if (url.includes("/settings")) return "Settings";
    if (url.includes("/users")) return "Admin";
    if (url.includes("/guide")) return "User guide";
    if (url.includes("/portfolio-view/"))
      return this.portfolio?.name || "Portfolio";
    if (url.includes("/portfolio-create")) return "New portfolio";
    if (url.includes("/portfolio"))
      return (
        this.portfolio?.name ||
        (this.portfolio?.id ? "Edit portfolio" : "New portfolio")
      );
    return "Portfolios";
  }

}
